import React from "react";
import { AlertTriangle, Loader2, MapPin, Phone } from "lucide-react";
import BottomSheet from "../../ui/bottom-sheet";
import { Button } from "../../ui/button";
import { ICON_STROKE } from "../../ui/icon";

const CallRevealConfirmModal = ({
  open,
  phase,
  contactLabel,
  locationStatus,
  error,
  onClose,
  onContinue,
  onShareLocation,
  onSkipLocation,
  onPlaceCall,
  turnstileRef,
  reserveBottomChrome = false,
  t,
}) => {
  const calling = phase === "calling";
  const isDial = phase === "dial";
  const showError = phase === "error" && error;
  const isIntro = phase === "intro";
  const isLocation = phase === "location";
  const waitingGps = isLocation && locationStatus === "waiting";

  let primaryAction;
  let secondaryAction;
  if (isIntro) {
    secondaryAction = { label: t("shared.cancel"), onClick: onClose };
    primaryAction = { label: t("public.callRevealContinue"), onClick: onContinue };
  } else if (isLocation && !waitingGps && !calling) {
    secondaryAction = { label: t("public.callRevealSkipLocation"), onClick: onSkipLocation };
    primaryAction = { label: t("public.callRevealShareLocation"), onClick: onShareLocation };
  } else if (isDial) {
    primaryAction = {
      label: contactLabel
        ? t("public.callRevealDialNow").replace("{{name}}", contactLabel)
        : t("public.callNow"),
      onClick: onPlaceCall,
    };
    secondaryAction = { label: t("shared.cancel"), onClick: onClose };
  }

  return (
    <BottomSheet
      open={open}
      onClose={calling || waitingGps || isDial ? undefined : onClose}
      title={t("public.callRevealTitle")}
      primaryAction={calling || waitingGps ? undefined : primaryAction}
      secondaryAction={calling || waitingGps ? undefined : secondaryAction}
      reserveBottomChrome={reserveBottomChrome}
    >
      <div className="space-y-4 text-slate-700">
        {isIntro && (
          <>
            <div className="flex gap-3 rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm">
              <MapPin className="shrink-0 text-sky-700" size={20} strokeWidth={ICON_STROKE} aria-hidden />
              <p>{t("public.callRevealLocationIntro")}</p>
            </div>
            <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
              <AlertTriangle className="shrink-0 text-amber-700" size={20} strokeWidth={ICON_STROKE} aria-hidden />
              <p>{t("public.callRevealBody")}</p>
            </div>
            <div className="flex flex-col gap-2 pt-1 sm:hidden">
              <Button fullWidth onClick={onContinue}>
                {t("public.callRevealContinue")}
              </Button>
              <Button variant="outline" fullWidth onClick={onClose}>
                {t("shared.cancel")}
              </Button>
            </div>
          </>
        )}

        {isLocation && !calling && (
          <div className="space-y-3 text-sm">
            <p>{t("public.callRevealLocationPrompt")}</p>
            {waitingGps && (
              <p className="flex items-center gap-2 text-slate-600" role="status">
                <Loader2 className="animate-spin shrink-0" size={18} aria-hidden />
                {t("public.callRevealLocationWaiting")}
              </p>
            )}
            {locationStatus === "denied" && (
              <p className="text-amber-800">{t("public.callRevealLocationDenied")}</p>
            )}
            {locationStatus === "timeout" && (
              <p className="text-amber-800">{t("public.callRevealLocationTimeout")}</p>
            )}
          </div>
        )}

        {contactLabel && (isIntro || calling) && (
          <p className="flex items-center gap-2 text-base font-semibold text-slate-900">
            <Phone size={18} strokeWidth={ICON_STROKE} aria-hidden />
            {t("public.callRevealContact").replace("{{name}}", contactLabel)}
          </p>
        )}

        {calling && (
          <p className="flex items-center gap-2 text-sm text-slate-600" role="status">
            <Loader2 className="animate-spin" size={18} aria-hidden />
            {t("public.callRevealWorking")}
          </p>
        )}

        {isDial && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-900">
            <p className="m-0 font-medium">{t("public.callRevealDialHint")}</p>
          </div>
        )}

        {showError && <p className="text-sm text-red-700">{error}</p>}
      </div>
      <div ref={turnstileRef} className="sr-only" aria-hidden />
    </BottomSheet>
  );
};

export default CallRevealConfirmModal;
